import {
  CheckCircledIcon,
  Cross2Icon,
  ImageIcon,
  Link2Icon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  ACCEPTED_GALLERY_PHOTO_TYPES,
  MAX_GALLERY_PHOTO_BYTES,
} from '../../../../app/hooks/gallery/useGalleryPhotoUpload';
import { Button } from '../../Button';
import EmptyState from '../../EmptyState';
import ImageConsentNotice from '../../Event/ImageConsentNotice';
import FileDropZone from '../../FileDropZone';
import { IconButton } from '../../IconButton';
import Loader from '../../Loader';
import ProgressBar from '../../ProgressBar';
import ScrollArea from '../../ScrollArea';
import { useGalleryManagerModalController } from './useGalleryManagerModalController';

interface IGalleryManagerModalProps {
  galleryId: string;
}

const ACCEPTED_GALLERY_PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

export default function GalleryManagerModal({
  galleryId,
}: IGalleryManagerModalProps) {
  const { t } = useTranslation();
  const {
    gallery,
    isGalleryLoading,
    imageConsent,
    hasLinkedEvent,
    leadHasDeniedConsent,
    queue,
    handleFilesSelected,
    clearFinished,
    removeItem,
    handleRemovePhoto,
    handlePublish,
    handleArchive,
    handleDelete,
    handleCopyLink,
  } = useGalleryManagerModalController(galleryId);

  if (isGalleryLoading || !gallery) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <Loader />
      </div>
    );
  }

  const photos = gallery.photos ?? [];
  const selectedCount = photos.filter((photo) => photo.selected).length;
  const selectionLimit = gallery.selectionLimit;
  const extrasCount =
    selectionLimit !== null ? Math.max(0, selectedCount - selectionLimit) : 0;
  const hasFinishedUploads = queue.some((item) => item.status === 'success');

  return (
    <div className="mt-2 flex min-h-0 flex-1 flex-col gap-4">
      <ImageConsentNotice
        consent={imageConsent}
        hasLinkedEvent={hasLinkedEvent}
        leadHasDeniedConsent={leadHasDeniedConsent}
      />

      <div className="flex flex-wrap items-center justify-end gap-2">
        {gallery.status === 'DRAFT' && (
          <Button
            className="h-9 w-auto px-4 text-sm"
            color="primary"
            onClick={handlePublish}
          >
            {t('gallery.manageModal.publish')}
          </Button>
        )}
        {gallery.status !== 'ARCHIVED' && (
          <Button
            className="h-9 w-auto px-4 text-sm"
            color="ghost"
            onClick={handleArchive}
          >
            {t('gallery.manageModal.archive')}
          </Button>
        )}
        <Button
          className="h-9 w-auto px-4 text-sm"
          color="danger"
          onClick={handleDelete}
        >
          {t('gallery.manageModal.delete')}
        </Button>
      </div>

      {gallery.status === 'PUBLISHED' && gallery.publicUrl && (
        <div className="bg-surface-card border-border-default flex flex-col gap-2 rounded-lg border p-3">
          <span className="text-text-secondary text-xs font-medium">
            {t('gallery.manageModal.publicUrlLabel')}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-text-primary min-w-0 flex-1 truncate text-sm">
              {gallery.publicUrl}
            </span>
            <Button
              className="h-9 w-auto shrink-0 gap-2 px-3 text-xs"
              color="outline"
              onClick={handleCopyLink}
            >
              <Link2Icon className="h-4 w-4" />
              {t('gallery.manageModal.copyLink')}
            </Button>
          </div>
        </div>
      )}

      {gallery.status === 'PUBLISHED' && (
        <div className="text-text-secondary flex flex-col gap-1 text-sm">
          {gallery.selectionConfirmedAt ? (
            <>
              <span className="text-text-primary font-medium">
                {t('gallery.manageModal.selectedSummary', {
                  selected: selectedCount,
                  total: photos.length,
                })}
                {extrasCount > 0 &&
                  ` (${t('gallery.extrasCount', { count: extrasCount })})`}
              </span>
              <span className="text-text-tertiary text-xs">
                {t('gallery.section.confirmedAt', {
                  date: format(
                    new Date(gallery.selectionConfirmedAt),
                    'dd MMM yyyy HH:mm',
                  ),
                })}
              </span>
            </>
          ) : (
            <span className="text-text-tertiary text-xs">
              {t('gallery.manageModal.selectionPending')}
            </span>
          )}
        </div>
      )}

      <div className="flex shrink-0 flex-col gap-2">
        <FileDropZone
          multiple
          onFilesSelected={handleFilesSelected}
          acceptedTypes={ACCEPTED_GALLERY_PHOTO_TYPES}
          acceptedExtensions={ACCEPTED_GALLERY_PHOTO_EXTENSIONS}
          maxSizeBytes={MAX_GALLERY_PHOTO_BYTES}
          placeholder={t('gallery.manageModal.uploadButton')}
          hint={t('gallery.manageModal.uploadHint')}
          inputLabel={t('gallery.manageModal.uploadButton')}
        />

        {queue.length > 0 && (
          <ul className="flex max-h-40 flex-col gap-2 overflow-y-auto">
            {queue.map((item) => (
              <li
                key={item.id}
                className="bg-surface-input flex items-center gap-3 rounded-md p-2"
              >
                <span className="text-text-primary min-w-0 flex-1 truncate text-xs">
                  {item.name}
                </span>
                {item.status === 'uploading' && (
                  <ProgressBar
                    value={item.progress}
                    showLabel={false}
                    className="w-24"
                  />
                )}
                {item.status === 'success' && (
                  <CheckCircledIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
                {item.status === 'error' && (
                  <span className="text-xs text-red-500 dark:text-red-400">
                    {t(
                      `gallery.manageModal.uploadErrors.${item.errorKey ?? 'uploadFailed'}`,
                    )}
                  </span>
                )}
                {item.status !== 'uploading' && (
                  <IconButton
                    size="sm"
                    aria-label={t('gallery.manageModal.removePhoto')}
                    onClick={() => removeItem(item.id)}
                  >
                    <Cross2Icon className="h-4 w-4" />
                  </IconButton>
                )}
              </li>
            ))}
            {hasFinishedUploads && (
              <Button
                className="h-8 w-auto self-end px-3 text-xs"
                color="ghost"
                onClick={clearFinished}
              >
                {t('gallery.manageModal.clearFinished')}
              </Button>
            )}
          </ul>
        )}
      </div>

      <ScrollArea className="min-h-0 flex-1">
        {photos.length === 0 ? (
          <EmptyState className="md:h-24 md:w-24">
            <p className="text-text-secondary text-sm">
              {t('gallery.manageModal.emptyPhotos')}
            </p>
          </EmptyState>
        ) : (
          <ul className="grid grid-cols-2 gap-2 p-1 sm:grid-cols-3 md:grid-cols-4">
            {photos.map((photo) => (
              <li
                key={photo.id}
                className="group border-border-default bg-surface-input relative aspect-square overflow-hidden rounded-md border"
              >
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="text-text-tertiary flex h-full w-full items-center justify-center">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}

                {photo.selected && (
                  <span className="absolute top-1 left-1 rounded-full bg-green-600 p-0.5 text-white dark:bg-green-500">
                    <CheckCircledIcon className="h-4 w-4" />
                  </span>
                )}

                <button
                  type="button"
                  aria-label={t('gallery.manageModal.removePhoto')}
                  onClick={() => handleRemovePhoto(photo.id)}
                  className="bg-grey-800/70 text-white-0 hover:bg-grey-800/90 absolute top-1 right-1 flex h-8 w-8 items-center justify-center rounded-full opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
                >
                  <Cross2Icon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}
